import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Leaf, Clock, FileDown, Users, ShoppingCart } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

// Public read-only view of a shared plan (no auth, no names, no health data, no prices)
export default function ShareView() {
  const { token } = useParams();
  const { t, tv, lang } = useLang();
  const [active, setActive] = useState(0);
  const { data, isLoading, isError } = useQuery({ queryKey: ["shared", token], queryFn: async () => (await api.get(`/share/${token}`)).data, retry: false });
  const day = data?.days?.[active];
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/pdf?lang=${lang}`;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-[#FAF6F0]/90 backdrop-blur-md border-b border-border">
        <div className="mx-auto max-w-5xl px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="share-logo">
            <span className="grid place-items-center h-9 w-9 rounded-2xl bg-primary text-primary-foreground"><Leaf className="h-5 w-5" /></span>
            <span className="font-serif text-2xl font-semibold tracking-tight">CHENIA</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            {data && <Button asChild size="sm" className="rounded-full bg-primary hover:bg-chenia-terracottadark"><a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="shared-pdf"><FileDown className="h-4 w-4 mr-1" /> PDF</a></Button>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-8 py-8">
        {isLoading && <p className="text-muted-foreground">{t("loading")}</p>}
        {isError && (
          <div className="chenia-card p-8 text-center" data-testid="shared-not-found">
            <p className="font-semibold">{t("share_not_found")}</p>
            <Button asChild className="mt-5 rounded-full bg-primary hover:bg-chenia-terracottadark"><Link to="/">{t("create_my_plan")}</Link></Button>
          </div>
        )}
        {data && (
          <div className="chenia-fade-up" data-testid="shared-plan">
            <p className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">{t("shared_by")}</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{t("shared_plan_title")}</h1>
            <p className="mt-2 text-muted-foreground inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> {data.n_members} {t("people")} · {data.meals_per_day} {t("meals_per_day_label")}</p>

            <div className="mt-6 grid grid-cols-7 gap-1.5 sm:flex sm:gap-2 pb-2" data-testid="shared-day-tabs">
              {DAYS.map((dk, i) => (
                <button key={dk} onClick={() => setActive(i)} data-testid={`shared-day-${dk}`}
                  className={`min-w-0 px-1 sm:px-4 py-2.5 rounded-2xl text-xs sm:text-sm font-medium text-center transition-colors ${active === i ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-secondary"}`}>
                  {t(dk).slice(0, 3)}
                </button>
              ))}
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {day?.meals.map((m) => (
                <div key={m.slot} className="chenia-card overflow-hidden" data-testid={`shared-meal-${m.slot}`}>
                  <div className="relative h-36">
                    <img src={m.image} alt="" className="h-full w-full object-cover" style={{ objectPosition: m.focal || "50% 50%" }} />
                    <span className="absolute top-3 left-3 rounded-full bg-background/90 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-wide">{t(m.slot)}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold leading-snug">{tv(m.name)}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground inline-flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {m.total_time} {t("min")}</span>
                      <span>{m.portions} {m.portions > 1 ? t("portions") : t("portion")}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="mt-10 font-serif text-2xl sm:text-3xl font-semibold tracking-tight inline-flex items-center gap-2"><ShoppingCart className="h-6 w-6 text-primary" /> {t("nav_grocery")}</h2>
            <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="shared-grocery">
              {data.grocery.aisles.map((a) => (
                <div key={a.aisle} className="chenia-card p-5">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">{t(`aisle_${a.aisle}`)}</p>
                  <ul className="space-y-1.5">
                    {a.items.map((it) => (
                      <li key={it.key} className="flex items-center justify-between gap-2 text-sm border-b border-border/50 last:border-0 pb-1.5">
                        <span>{tv(it.name)}</span>
                        <span className="text-muted-foreground shrink-0">{it.qty} {it.unit} · {it.packs} {t("packs")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{t("disclaimer")}</p>
          </div>
        )}
      </main>
    </div>
  );
}
